import { Link } from 'react-router-dom';
import { Minus, Plus, Trash2, ShoppingBag } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useCart } from '@/context/CartContext';
import { useAuth } from '@/context/AuthContext';

export default function CartPage() {
  const { cart, cartTotal, updateQuantity, removeFromCart, loading } = useCart();
  const { isAuthenticated } = useAuth();

  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(price);
  };

  const shipping = cartTotal >= 100 ? 0 : 10;
  const tax = cartTotal * 0.08;
  const total = cartTotal + shipping + tax;

  if (loading) {
    return (
      <div className="pt-24 md:pt-28 min-h-screen container-custom">
        <div className="animate-pulse space-y-4">
          {[...Array(3)].map((_, i) => (
            <div key={i} className="h-32 bg-neutral-900" />
          ))}
        </div>
      </div>
    );
  }

  if (cart.length === 0) {
    return (
      <div className="pt-24 md:pt-28 min-h-screen container-custom" data-testid="empty-cart">
        <div className="text-center py-20">
          <ShoppingBag size={64} className="mx-auto text-neutral-700 mb-6" />
          <h1 className="font-['Syne'] text-3xl font-bold mb-4">Your cart is empty</h1>
          <p className="text-muted-foreground mb-8">
            Looks like you haven't added anything to your cart yet
          </p>
          <Link to="/shop">
            <Button className="btn-primary rounded-none" data-testid="continue-shopping-btn">
              Continue Shopping
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="pt-24 md:pt-28 min-h-screen" data-testid="cart-page">
      <div className="container-custom">
        <h1 className="font-['Syne'] text-3xl md:text-4xl font-bold mb-8">Shopping Cart</h1>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Cart Items */}
          <div className="lg:col-span-2 space-y-4">
            {cart.map((item, idx) => (
              <div 
                key={`${item.product_id}-${item.size}-${item.color}`}
                className="flex gap-4 p-4 border border-neutral-800"
                data-testid={`cart-item-${idx}`}
              >
                <Link to={`/product/${item.product_id}`} className="w-24 h-24 flex-shrink-0">
                  <img
                    src={item.product?.images?.[0] || 'https://via.placeholder.com/100'}
                    alt={item.product?.name}
                    className="w-full h-full object-cover"
                  />
                </Link>
                <div className="flex-1">
                  <Link to={`/product/${item.product_id}`}>
                    <h3 className="font-medium hover:text-[#FF3B30] transition-colors">
                      {item.product?.name}
                    </h3>
                  </Link>
                  <p className="text-muted-foreground text-sm mt-1">
                    {item.color} / {item.size}
                  </p>
                  <p className="font-medium mt-2">{formatPrice(item.product?.price || 0)}</p>
                </div>
                <div className="flex flex-col items-end justify-between">
                  <button
                    onClick={() => removeFromCart(item.product_id, item.size, item.color)}
                    className="text-muted-foreground hover:text-[#FF3B30] transition-colors"
                    data-testid={`remove-item-${idx}`}
                  >
                    <Trash2 size={18} />
                  </button>
                  {isAuthenticated && (
                    <div className="flex items-center border border-neutral-700">
                      <button
                        onClick={() => updateQuantity(item.product_id, item.quantity - 1, item.size, item.color)}
                        className="p-2 hover:bg-neutral-800"
                        data-testid={`decrease-qty-${idx}`}
                      >
                        <Minus size={14} />
                      </button>
                      <span className="w-8 text-center text-sm">{item.quantity}</span>
                      <button
                        onClick={() => updateQuantity(item.product_id, item.quantity + 1, item.size, item.color)}
                        className="p-2 hover:bg-neutral-800"
                        data-testid={`increase-qty-${idx}`}
                      >
                        <Plus size={14} />
                      </button>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* Order Summary */}
          <div className="lg:col-span-1">
            <div className="border border-neutral-800 p-6 sticky top-24">
              <h2 className="font-['Syne'] text-xl font-bold mb-6">Order Summary</h2>
              
              <div className="space-y-4 pb-4 border-b border-neutral-800">
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Subtotal</span>
                  <span>{formatPrice(cartTotal)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Shipping</span>
                  <span>{shipping === 0 ? 'FREE' : formatPrice(shipping)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Estimated Tax</span>
                  <span>{formatPrice(tax)}</span>
                </div>
              </div>

              <div className="flex justify-between py-4 text-lg font-bold">
                <span>Total</span>
                <span data-testid="cart-total">{formatPrice(total)}</span>
              </div>

              {cartTotal < 100 && (
                <p className="text-sm text-muted-foreground mb-4">
                  Add {formatPrice(100 - cartTotal)} more for free shipping
                </p>
              )}

              <Link to="/checkout">
                <Button className="btn-primary rounded-none w-full" data-testid="checkout-btn">
                  Proceed to Checkout
                </Button>
              </Link>

              <Link to="/shop" className="block text-center mt-4 text-sm text-muted-foreground hover:text-white">
                Continue Shopping
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
